import { useEffect } from "react";
import type { AbstractAgent } from "@ag-ui/client";
import { DatasetListResponse } from "datasets/types/interfaces/datasetListItem";
import { useSWRConfig } from "swr";
import { catalogDatasetsListUrl } from "~/services/catalog";
import { projectDatasetsPath } from "~/services/projects";

/**
 * Re-validate the project's dataset list when an agent run ends, so a dataset
 * an agent creates and attaches to the project server-side (writes SWR can't
 * observe) shows up in the nextgen `SessionContextSidebar` without a manual
 * refresh.
 *
 * The sidebar resolves its datasets by intersecting the project's attached-id
 * list (`projectDatasetsPath`) with the full catalog list (`catalogDatasetsListUrl`).
 * The id list is cheap, so it's refreshed after every run; the catalog list is
 * up to 1000 records, so it's refreshed only when the refreshed id list contains
 * an id the cached catalog list can't resolve — i.e. a freshly created dataset —
 * sparing the heavy refetch on ordinary turns.
 *
 * Keyed off the terminal run event (rather than `onToolCallEndEvent`) on purpose:
 * a tool like `create_dataset` provisions a schema and attaches mid-turn, well
 * after the tool-call args finish streaming, so only the run's end guarantees the
 * writes have landed — and it keeps this decoupled from the tool's name.
 */
export function useProjectDatasetSync(agent: AbstractAgent, projectId: string | undefined) {
  const { cache, mutate } = useSWRConfig();
  useEffect(() => {
    if (!projectId) return;
    const revalidate = async () => {
      try {
        const ids = (await mutate(projectDatasetsPath(projectId))) as string[] | undefined;
        if (!ids?.length) return;
        const cached = cache.get(catalogDatasetsListUrl)?.data as DatasetListResponse | undefined;
        const known = new Set((cached?.items ?? []).map((d) => d.id));
        if (ids.some((id) => !known.has(id))) {
          await mutate(catalogDatasetsListUrl);
        }
      } catch (err) {
        console.error("[useProjectDatasetSync] Failed to refresh project datasets:", err);
      }
    };
    const onRunEnd = () => void revalidate();
    const sub = agent.subscribe({
      onRunFinishedEvent: onRunEnd,
      onRunErrorEvent: onRunEnd,
    });
    return () => sub.unsubscribe();
  }, [agent, projectId, cache, mutate]);
}
